import { Inbox, CalendarDays, CalendarRange, CheckCircle2, Receipt, AlertTriangle, Clock, ChevronRight } from "lucide-react";
import { STATUS, euro, eventSlot, fmtDateLong } from "../admin/calendar/calendarUtils";
import { RequestCard } from "./RequestCard";

const Tile = ({ icon: Icon, label, value, tone, onClick, testId }) => (
  <button type="button" onClick={onClick} className={`text-left bg-[#121212] border p-3 active:scale-[0.98] transition-transform ${tone}`} data-testid={testId}>
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider opacity-80"><Icon className="w-3.5 h-3.5" />{label}</div>
    <div className="font-['Bebas_Neue'] text-3xl leading-tight">{value}</div>
  </button>
);

export const MiniEvent = ({ e, onOpen, showDate }) => {
  const slot = eventSlot(e);
  const s = STATUS[e.display_status] || STATUS.pending;
  return (
    <button type="button" onClick={() => onOpen(e)} className="w-full text-left flex items-center gap-3 bg-[#121212] border border-white/5 p-3 active:bg-white/5" data-testid={`mini-event-${e.appointment_id}`}>
      <span className={`w-2.5 h-10 ${s.dot}`} />
      <span className="flex-1 min-w-0">
        <span className="block text-white font-medium truncate">{slot.time} · {e.customer?.name}</span>
        <span className="block text-xs text-[#A1A1AA] truncate">{showDate ? `${fmtDateLong(slot.date)} · ` : ""}{e.training_name} · {e.duration} Min</span>
      </span>
      <ChevronRight className="w-4 h-4 text-[#71717A]" />
    </button>
  );
};

export function DashboardView({ token, data, onOpen, onAlternative, onChanged, go }) {
  if (!data) return <p className="text-center text-[#71717A] py-16">Laden…</p>;
  const { stats, requests, today, next_7_days: week, invoices } = data;
  const pending = requests.filter((r) => r.status === "pending");
  const n = stats.next_appointment;
  return (
    <div className="space-y-5" data-testid="mobile-dashboard">
      {pending.length > 0 ? (
        <section>
          <button type="button" onClick={() => go("requests")} className="w-full flex items-center justify-between bg-yellow-400 text-black px-4 py-3 font-bold" data-testid="dash-new-requests-banner">
            <span className="flex items-center gap-2"><Inbox className="w-5 h-5" />{pending.length} neue Anfrage{pending.length > 1 ? "n" : ""}</span><ChevronRight className="w-5 h-5" />
          </button>
          <div className="mt-2"><RequestCard token={token} e={pending[0]} onChanged={onChanged} onAlternative={onAlternative} highlight /></div>
        </section>
      ) : (
        <div className="bg-[#121212] border border-white/10 p-4 text-sm text-[#A1A1AA]" data-testid="dash-no-requests">Keine offenen Anfragen – alles erledigt.</div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Tile icon={Inbox} label="Offene Anfragen" value={stats.open_requests} tone="border-yellow-400/40 text-yellow-300" onClick={() => go("requests")} testId="dash-open" />
        <Tile icon={CalendarDays} label="Heute" value={stats.today} tone="border-white/10 text-white" onClick={() => go("calendar")} testId="dash-today" />
        <Tile icon={CalendarRange} label="Nächste 7 Tage" value={week.length} tone="border-white/10 text-white" onClick={() => go("calendar")} testId="dash-week" />
        <Tile icon={CheckCircle2} label="Bestätigt" value={stats.confirmed} tone="border-green-500/40 text-green-400" onClick={() => go("calendar")} testId="dash-confirmed" />
        <Tile icon={Receipt} label={`Offene Rechnungen`} value={`${invoices.open_count}`} tone="border-white/10 text-white" onClick={() => go("invoices")} testId="dash-invoices-open" />
        <Tile icon={AlertTriangle} label="Überfällig" value={invoices.overdue_count} tone="border-red-500/40 text-red-400" onClick={() => go("invoices")} testId="dash-invoices-overdue" />
      </div>
      <p className="text-xs text-[#71717A] -mt-3">Offen gesamt {euro(invoices.open_sum)} · davon überfällig {euro(invoices.overdue_sum)}</p>
      <div className="bg-[#c1ff72]/10 border border-[#c1ff72]/30 p-3 flex items-center gap-3" data-testid="dash-next">
        <Clock className="w-5 h-5 text-[#c1ff72]" />
        <div className="text-sm"><div className="text-[11px] uppercase tracking-wider text-[#c1ff72]">Nächster Termin</div>
          {n ? <button type="button" onClick={() => onOpen(n)} className="text-white text-left">{fmtDateLong(eventSlot(n).date)}, {eventSlot(n).time} Uhr · {n.customer?.name}</button> : <span className="text-[#71717A]">Kein bestätigter Termin</span>}
        </div>
      </div>
      <section>
        <h3 className="text-xs uppercase tracking-widest text-[#71717A] mb-2">Heute</h3>
        <div className="space-y-1.5">{today.length ? today.map((e) => <MiniEvent key={e.appointment_id} e={e} onOpen={onOpen} />) : <p className="text-sm text-[#71717A]">Heute keine Termine.</p>}</div>
      </section>
      <section>
        <h3 className="text-xs uppercase tracking-widest text-[#71717A] mb-2">Nächste 7 Tage</h3>
        <div className="space-y-1.5">{week.length ? week.map((e) => <MiniEvent key={e.appointment_id} e={e} onOpen={onOpen} showDate />) : <p className="text-sm text-[#71717A]">Keine Termine in den nächsten 7 Tagen.</p>}</div>
      </section>
    </div>
  );
}

export function RequestsView({ token, data, onChanged, onAlternative }) {
  if (!data) return <p className="text-center text-[#71717A] py-16">Laden…</p>;
  return (
    <div className="space-y-3" data-testid="mobile-requests">
      {!data.requests.length && <p className="text-center text-[#71717A] py-12" data-testid="requests-empty">Keine offenen Anfragen.</p>}
      {data.requests.map((e) => <RequestCard key={e.appointment_id} token={token} e={e} onChanged={onChanged} onAlternative={onAlternative} highlight={e.status === "pending"} />)}
    </div>
  );
}
